import { create } from "zustand";
import {
  ResolvedSearchBy,
  SearchBy,
  UserAdminInfo,
  UserResource,
  UsersListResponse,
} from "../api/users";

/** One term matched one way. The API owns which field the term is matched on. */
export interface UserSearch {
  q?: string;
  searchBy: SearchBy;
}

/** Filters narrow what a search returned; they never replace the search. */
export interface UserFilters {
  rcStore?: string;
  rcStatus?: string;
}

export const EMPTY_SEARCH: UserSearch = { searchBy: "auto" };

interface UsersState {
  users: UserAdminInfo[];
  nextToken?: string;
  loading: boolean;
  error: string | null;
  search: UserSearch;
  filters: UserFilters;
  /** Which mode the API resolved the term to; undefined when listing. */
  resolvedSearchBy?: ResolvedSearchBy;
  /** True when the result is a prefix of the matches rather than all of them. */
  truncated: boolean;
  loadUsers: (search?: UserSearch, filters?: UserFilters) => Promise<void>;
  loadNextPage: () => Promise<void>;
}

const fetchUsers = (
  search: UserSearch,
  filters: UserFilters,
  nextToken?: string
): Promise<UsersListResponse> => {
  // With no term there is nothing to match, so the mode is left off entirely:
  // a plain listing should not go out looking like a search.
  const term = search.q?.trim()
    ? { q: search.q.trim(), searchBy: search.searchBy }
    : {};

  // The RevenueCat provider/status filters need per-user RevenueCat lookups, so
  // they have their own endpoint; it forwards the same search term.
  if (filters.rcStore || filters.rcStatus) {
    return UserResource.getUsersByRcProvider({
      store: filters.rcStore,
      status: filters.rcStatus,
      ...term,
      nextToken,
    });
  }

  return UserResource.getList({ ...term, nextToken });
};

/**
 * Identifies the most recent request. Search modes differ in latency by orders
 * of magnitude — an id lookup is a point read while a display-name search
 * scans — so a slow earlier response can land after a fast later one. Only the
 * newest request is allowed to write its result.
 */
let latestRequestId = 0;

export const useUsersStore = create<UsersState>((set, get) => ({
  users: [],
  nextToken: undefined,
  loading: false,
  error: null,
  search: EMPTY_SEARCH,
  filters: {},
  resolvedSearchBy: undefined,
  truncated: false,

  loadUsers: async (
    search: UserSearch = EMPTY_SEARCH,
    filters: UserFilters = {}
  ) => {
    const requestId = ++latestRequestId;
    set({ loading: true, error: null, search, filters });
    try {
      const response = await fetchUsers(search, filters);
      if (requestId !== latestRequestId) return;
      set({
        users: response.users,
        nextToken: response.nextToken,
        resolvedSearchBy: response.resolvedSearchBy,
        truncated: Boolean(response.truncated),
        loading: false,
      });
    } catch (err) {
      if (requestId !== latestRequestId) return;
      const error = err instanceof Error ? err.message : "Failed to load users";
      set({ error, loading: false });
    }
  },

  loadNextPage: async () => {
    const { nextToken, search, filters, users } = get();
    // Single-user lookups and display-name searches come back without a token,
    // so the token alone decides whether there is a page two.
    if (!nextToken) return;
    const requestId = ++latestRequestId;
    set({ loading: true });
    try {
      const response = await fetchUsers(search, filters, nextToken);
      // A new search started while this page was in flight; appending it would
      // splice the old result set onto the new one.
      if (requestId !== latestRequestId) return;
      set({
        users: [...users, ...response.users],
        nextToken: response.nextToken,
        truncated: Boolean(response.truncated),
        loading: false,
      });
    } catch (err) {
      if (requestId !== latestRequestId) return;
      const error =
        err instanceof Error ? err.message : "Failed to load more users";
      set({ error, loading: false });
    }
  },
}));
